import React, { Component } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import './accountProfileLayout.css';
import { Layout } from 'antd';
import { LinkContainer } from 'react-router-bootstrap'
import { UserOutlined, BankOutlined, TeamOutlined, LogoutOutlined, SafetyOutlined, ApiOutlined } from '@ant-design/icons';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import firebase from 'firebase/app';
import { Redirect } from 'react-router-dom';

import { ccyIsoCodeToSymbolMap, countryFlags } from '../../utils/CurrencyUtils';
import StrikeFooter from '../../components/StrikeFooter';
import { getMaxAccountCcyTotal } from '../../utils/AccountCalculations';
import StrikeClientModel from '../../models/StrikeClientModel';
import StrikeAccountTransactionsModel from '../../models/StrikeAccountTransactionsModel';
import placeholderImage from '../../images/placeholder_avatar.png';
import { getSidebarLogoLg, getBrandIconDark, getBrandName } from '../../utils/BrandConfig';
import { DelegatedViewingBanner } from '../../components/client/DelegatedViewingBanner';
import { TestModeBanner, TEST_MODE_CLIENT_COLOR } from '../../components/client/TestModeBanner';
import { isTestModeOn, TEST_MODE_EVENT } from '../../utils/testMode';
import { useMcpLive } from '../../utils/aiConnections';


const { Content } = Layout;

const ccy = ccyIsoCodeToSymbolMap;

/** The AI connections entry renders only when the payer says MCP is live (one switch). */
function AiConnectionsNavItem({ to, dropdown }) {
  const live = useMcpLive();
  if (live !== true) return null;
  return (
    <LinkContainer to={to}>
      {dropdown
        ? <NavDropdown.Item><ApiOutlined /> AI connections</NavDropdown.Item>
        : <Nav.Link className="d-lg-none"><ApiOutlined /> AI connections</Nav.Link>}
    </LinkContainer>
  );
}

const CountryFlagIcon = ({ client }) => {
  const clientCountry = client && client.country ? client.country : null;
  const flag = clientCountry ? countryFlags[clientCountry] : null;
  return flag ? <>{flag}</> : <UserOutlined />;
}

export default class AccountProfileLayout extends Component {

  constructor(props) {
    super(props);

    const clientId = this.props.match.params.clientId;

    this.baseURLs = props.baseURLs || {
      manage: '/client/manage',
      login: '/client/login'
    };

    this.clientModel = new StrikeClientModel({
      onClientFound: this.foundClient,
      onClientNotFound: this.clientNotFound,
      viewModel: this,
      clientId,
      isAdmin: false,
    });

    this.accountsModel = new StrikeAccountTransactionsModel({
      isTestMode: isTestModeOn(clientId),
      callback: this.updatedDataSource,
      viewModel: this,
      clientId
    });

    this.state = {
      clientId: clientId,
      triggeredLogOut: false,
      isUseDashboard: false,
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.clientModel.waitForClientData();
    // Chrome follows the Test Mode switch live (banner + purple navbar).
    this.onTestModeChanged = () => { if (this._isMounted) this.forceUpdate(); };
    window.addEventListener(TEST_MODE_EVENT, this.onTestModeChanged);
  }

  componentWillUnmount() {
    if (this.clientModel) this.clientModel.stop();
    if (this.accountsModel) this.accountsModel.stop();
    if (this.onTestModeChanged) window.removeEventListener(TEST_MODE_EVENT, this.onTestModeChanged);
    this._isMounted = false;
  }

  foundClient = async (client) => {
    // Prevent setState on unmounted component
    if (!this._isMounted) return;

    this.setState(
      (state) => {
        return {
          clientNotFound: false,
          client,
          isUseDashboard: client ? !!client.isUseDashboard : false,
          loadingDetails: {
            client: false,
          }
        }
      }
    );

    this.accountsModel.waitForAccountsData();
  }

  clientNotFound = () => {
    console.log("No client found. Stay logged out.");

    // Prevent setState on unmounted component
    if (!this._isMounted) return;

    // This layout is also used for the claim flow and we want to avoid this issue at this check
    if (this.props.match.path !== "/client/claim/:tagId") {
      this.setState({
        clientNotFound: true
      });
    }
  }

  renderRedirectToLogin = () => {
    let toRoute = `${this.baseURLs.login}`;

    if (this.state.triggeredLogOut === false && this.props.match.path !== "/client/manage/:clientId" && this.props.match.path !== "/client/claim/:tagId") {
      const fullPath = this.props.match.url;
      const remainingRoute = fullPath.split(`${this.state.clientId}/`)[1];

      toRoute += `?redirectRoute=${remainingRoute}`;
    }

    return <Redirect to={toRoute} />
  }

  // Leave the page first, then sign out. Signing out with the dashboard still
  // mounted makes every live listener on it fail its rules check at once; in
  // production that is console noise, on localhost it is the dev error overlay
  // over a page that never moved. The redirect below unmounts the screen (and
  // this layout's own models) before the token goes.
  logOut = () => {
    this.setState({ triggeredLogOut: true }, () => {
      setTimeout(() => {
        firebase.auth().signOut().catch((err) => console.warn('[logout] signOut failed:', err && err.message));
      }, 0);
    });
  }

  updatedDataSource = async (txns) => {
    // Prevent setState on unmounted component
    if (!this._isMounted) return;

    const accounts = this.accountsModel.accounts;

    this.setState(
      (state) => {
        return {
          accounts,
        }
      }
    );
  }

  render() {
    const clientId = this.props.match.params.clientId;
    const displayName = this.state.client ? this.state.client.displayName : "Your Profile";
    const avatarSrc = this.state.client && this.state.client.avatar ? this.state.client.avatar : placeholderImage;
    const accounts = (this.state.accounts && this.state.accounts.length > 0) ? this.state.accounts : null;
    const mainBalance = accounts ? getMaxAccountCcyTotal(accounts) : null;
    const balance = mainBalance ? (ccy[mainBalance.currency] + mainBalance.decimalBalance.toFixed(2)) : null;
    const isUseDashboard = false;
    const logoLink = isUseDashboard ? `/teams/manage/${clientId}` : `/client/manage/${clientId}`;
    const isRestricted = this.state.client ? (this.state.client.status === 'paused') : true;
    const isTestWorld = isTestModeOn(clientId);

    const clientHomeLink = `/client/manage/${clientId}`;
    const profileLink = `/client/manage/${clientId}/profile`;
    const securityLink = `/client/manage/${clientId}/security`;
    const aiConnectionsLink = `/client/manage/${clientId}/ai-connections`;
    const payoutsLink = `/client/manage/${clientId}/bank`;
    const accessLink = `/client/manage/${clientId}/access`;

    if (this.state.triggeredLogOut) return this.renderRedirectToLogin();
    if (this.state.clientNotFound) return this.renderRedirectToLogin();

    return (
      <>
        <Navbar
          bg={isTestWorld ? undefined : 'primary'}
          variant="dark"
          expand="lg"
          collapseOnSelect
          // TEST world wears orange. bg-primary is a Bootstrap !important
          // utility — it must be ABSENT (not overridden) for the style to win.
          style={isTestWorld ? { backgroundColor: TEST_MODE_CLIENT_COLOR } : undefined}
        >
          <Navbar.Brand href={logoLink} id="navbar-logo" className="d-flex align-items-center">
            <img
              alt={getBrandName()}
              src={getBrandIconDark()}
              className="d-lg-none mr-auto"
              style={{ height: 36, width: 36, objectFit: 'contain' }}
            />
            <img
              alt={getBrandName()}
              src={getSidebarLogoLg()}
              className="d-none d-lg-block mr-auto"
              style={{ height: 36, maxWidth: 180, objectFit: 'contain' }}
            />
          </Navbar.Brand>

          {balance && (
            <div id="navbar-balance" className="d-flex justify-content-center mx-auto">
              <Nav.Link href={clientHomeLink} className="navbar-nav mx-auto text-center">
                <span className="badge badge-pill badge-light">{balance}</span>
              </Nav.Link>
            </div>
          )}

          <Navbar.Toggle aria-controls="basic-navbar-nav">
            <img className="rounded-circle"
              width="40" height="40"
              src={avatarSrc}
              alt={displayName}
            />
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav>
              {!isRestricted && (
                <>
                  <LinkContainer to={profileLink}>
                    <Nav.Link className="d-lg-none"><CountryFlagIcon client={this.state.client} /><span> Profile</span></Nav.Link>
                  </LinkContainer>
                  {/* Payouts is always reachable: a bank account can be added before the first tip. */}
                  <LinkContainer to={payoutsLink}>
                    <Nav.Link className="d-lg-none"><BankOutlined /> Payouts</Nav.Link>
                  </LinkContainer>
                  <LinkContainer to={accessLink}>
                    <Nav.Link className="d-lg-none"><TeamOutlined /> Access</Nav.Link>
                  </LinkContainer>
                  <LinkContainer to={securityLink}>
                    <Nav.Link className="d-lg-none"><SafetyOutlined /> Security</Nav.Link>
                  </LinkContainer>
                  <AiConnectionsNavItem to={aiConnectionsLink} />
                </>
              )}
              <Nav.Link onClick={this.logOut} className="d-lg-none"><LogoutOutlined /> Log out</Nav.Link>

              <NavDropdown title={
                <img className="rounded-circle"
                  width="40" height="40"
                  src={avatarSrc}
                  alt={displayName}
                />
              }
                id="basic-nav-dropdown" alignRight className="d-none d-lg-block">
                {!isRestricted && (
                  <>
                    <LinkContainer exact to={clientHomeLink}>
                      <NavDropdown.Item><strong>{displayName}</strong></NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to={profileLink}>
                      <NavDropdown.Item><CountryFlagIcon client={this.state.client} /><span> Profile</span></NavDropdown.Item>
                    </LinkContainer>
                    {/* Payouts is always reachable: a bank account can be added before the first tip. */}
                    <LinkContainer to={payoutsLink}>
                      <NavDropdown.Item>
                        <BankOutlined /> Payouts
                      </NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to={accessLink}>
                      <NavDropdown.Item><TeamOutlined /> Access</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to={securityLink}>
                      <NavDropdown.Item><SafetyOutlined /> Security</NavDropdown.Item>
                    </LinkContainer>
                    <AiConnectionsNavItem to={aiConnectionsLink} dropdown />
                    <NavDropdown.Divider />
                  </>
                )}
                <NavDropdown.Item onClick={this.logOut}><LogoutOutlined /> Log out</NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <Container fluid>
          <TestModeBanner client={this.state.client} />
          <DelegatedViewingBanner client={this.state.client} />
          <Content style={{ padding: '0 0 0 0' }}>
            <div className="site-layout-content">
              {this.props.children}
            </div>
          </Content>
          <StrikeFooter locStrings={this.props.locStrings} />
        </Container>
      </>
    );
  }
}
